import { z } from "zod";
import type { JwtPayload } from "jwt-decode";

export const CLAIMS = {
  userId: "https://id.about.golf/claims/v2/userId",
  userRoles: "https://id.about.golf/claims/v2/userRoles",
  facilities: "https://id.about.golf/claims/v2/facilities",
  email: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
  name: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
} as const;

// Helper function to parse comma-separated strings into arrays
const parseCommaSeparated = (value: string | undefined): string[] => {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

const zUuidArray = z.array(z.string().uuid()).default([]);

// Raw token schema - matches the actual JWT payload exactly
export const rawUserTokenSchema = z
  .object({
    // Required JWT payload fields
    iss: z.string(),
    sub: z.string(),
    iat: z.number().int(),
    nbf: z.number().int(),
    exp: z.number().int(),
    // Existing fields
    user: z.string().optional(),
    featureFlags: z.string().optional(),
    // Raw claim URLs
    [CLAIMS.userId]: z.string(),
    [CLAIMS.userRoles]: z.string().optional(),
    [CLAIMS.facilities]: z.string().optional(),
    [CLAIMS.email]: z.string().email().optional(),
    [CLAIMS.name]: z.string(),
  })
  .passthrough(); // Allow other JWT payload fields

// Parsed token schema - transforms raw token into app-friendly structure
export const userTokenSchema = rawUserTokenSchema.transform((raw) => {
  // Store the raw token before adding mapped properties
  const rawToken: z.infer<typeof rawUserTokenSchema> = { ...raw };

  const userRolesRaw = raw[CLAIMS.userRoles]
    ? parseCommaSeparated(raw[CLAIMS.userRoles])
    : [];

  const parsed = {
    // Required JWT payload fields
    iss: raw.iss,
    sub: raw.sub,
    iat: raw.iat,
    nbf: raw.nbf,
    exp: raw.exp,
    // Existing fields
    user: raw.user ?? "",
    featureFlags: raw.featureFlags ?? "",
    // Mapped properties
    userId: raw[CLAIMS.userId],
    userName: raw[CLAIMS.name],
    userRoles: zUuidArray.parse(userRolesRaw),
    email: raw[CLAIMS.email] ?? "",
    facilities: parseCommaSeparated(raw[CLAIMS.facilities]),
    // Include full raw payload
    raw: rawToken,
  };

  return parsed;
});

// Export inferred types
export type RawUserToken = JwtPayload & z.infer<typeof rawUserTokenSchema>;
export type UserToken = z.infer<typeof userTokenSchema>;
